/** Minimal typed event emitter (no dependency). Handlers that throw never break other handlers. */
type Handler = (...args: never[]) => void;
type ArgsOf<F> = F extends (...args: infer A) => void ? A : never;

export class Emitter<E extends object> {
  private handlers = new Map<keyof E, Set<Handler>>();

  on<K extends keyof E>(event: K, handler: E[K] & Handler): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
    };
  }

  emit<K extends keyof E>(event: K, ...args: ArgsOf<E[K]>): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of Array.from(set)) {
      try {
        (handler as unknown as (...a: ArgsOf<E[K]>) => void)(...args);
      } catch (err) {
        console.error(`[emitter] handler for ${String(event)} failed`, err);
      }
    }
  }

  clear(): void {
    this.handlers.clear();
  }
}
